<template>
  <KeyValuePair
    class="line-item-summary"
    :label="label"
    :value="tooltip"
    :uppercase-label="uppercaseLabel"
  >
    <template #value>
      <slot
        name="count"
        :count="lineItems.length"
        :formatted-count="formattedCount"
      >
        <router-link
          v-if="lineItemLinkRoute"
          :to="lineItemLinkRoute"
          class="labeled-icon"
        >
          <i class="pi pi-list" aria-hidden="true" />
          {{ formattedCount }}
        </router-link>
        <LabeledIcon v-else icon="pi pi-list" :label="formattedCount" />
      </slot>

      <LabeledIcon
        v-if="showHandlingUnits"
        icon="pi pi-shopping-bag"
        :label="formattedHandling"
      />

      <LabeledIcon icon="pi pi-briefcase" :label="formattedWeight" />
    </template>
  </KeyValuePair>
</template>

<script lang="ts" setup>
import type { PropType } from 'vue';
import { computed, toRefs } from 'vue';
import { useI18n } from 'vue-i18n';
import { RouteLocationRaw } from 'vue-router';

import i18n, { translateNumberedEntity } from '@rygen/rygen-ui/i18n';
import { WeightUom } from '@rygen/rygen-ui/model';

import { useLineItemsSummary } from '@/composables';
import { LineItem } from '@/model';

import LabeledIcon from '@/components/ui/LabeledIcon.vue';
import KeyValuePair from '@rygen/rygen-ui/components/ui/KeyValuePair.vue';

const props = defineProps({
  lineItems: {
    type: Array as PropType<LineItem[]>,
    default: () => [],
  },
  label: {
    type: String,
    default: () => i18n.global.t('lineItems.lineItems', 2),
  },
  lineItemLinkRoute: {
    type: Object as PropType<RouteLocationRaw>,
    default: () => undefined,
  },
  showHandlingUnits: {
    type: Boolean,
    default: () => false,
  },
  uppercaseLabel: {
    type: Boolean,
    default: () => false,
  },
});
const {
  lineItems,
  label,
  lineItemLinkRoute,
  showHandlingUnits,
  uppercaseLabel,
} = toRefs(props);

defineSlots<{
  count: (props: { count: number; formattedCount: string }) => any;
}>();

const { n } = useI18n();
const { handlingUnits, totalHandlingUnits, totalWeight } =
  useLineItemsSummary(lineItems);

const formattedWeight = computed(() => {
  return translateNumberedEntity(
    totalWeight.value,
    `uoms.weight.abbreviations.${WeightUom.POUND}`,
  );
});

const formattedHandling = computed(() => {
  const total = n(totalHandlingUnits.value, 'basic');
  return [total, handlingUnits.value].join(' ');
});

const formattedCount = computed(() => {
  return translateNumberedEntity(lineItems.value.length, 'lineItems.lineItems');
});

const tooltip = computed(() => {
  const numLineItemsOrHandlingUnits = showHandlingUnits.value
    ? formattedHandling.value
    : formattedCount.value;
  return `${formattedWeight.value}\n${numLineItemsOrHandlingUnits}`;
});
</script>

<style>
.line-item-summary.key-value-pair {
  .key-value-pair-value {
    display: flex;
    flex-direction: column;
    gap: var(--sp-2);
  }
}
</style>
